import { ReactElement } from 'react';

import { appColors } from '@backrs_ui/common-ui/src/assets/appColors';
import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { Graph } from '@backrs_ui/common-ui/src/atoms/Graph';
import { CareerPath } from '@backrs_ui/common-ui/src/models/careerPath';

import { Typography, Box, SxProps, Theme } from '@mui/material';

const CAREER_PATH_STRINGS = appStrings.modules.nugget.careerPath;

export const CareerPathData = ({
  careerPathInfo,
}: {
  careerPathInfo: CareerPath | null;
}): ReactElement => {
  return (
    <Box sx={containerStyle}>
      <Box sx={columnStyle}>
        <Box>
          <HighlightedText>{CAREER_PATH_STRINGS.earlyCareerHires}</HighlightedText>
          <Typography>
            {CAREER_PATH_STRINGS.skilled}:{' '}
            {careerPathInfo?.early_career_hires_per_year.skilled_technical?.toLocaleString()}
          </Typography>
          <Typography>
            {CAREER_PATH_STRINGS.professional}:{' '}
            {careerPathInfo?.early_career_hires_per_year.professional?.toLocaleString()}
          </Typography>
        </Box>

        <Box>
          <HighlightedText>{CAREER_PATH_STRINGS.growth}</HighlightedText>
          <Typography>{careerPathInfo?.growth}</Typography>
        </Box>

        <Box>
          <HighlightedText>{CAREER_PATH_STRINGS.ai_disruption}</HighlightedText>
          <Typography>{careerPathInfo?.ai_disruption}</Typography>
        </Box>

        <Box mb={{ xs: 2, md: 0 }}>
          <HighlightedText>{CAREER_PATH_STRINGS.insight}</HighlightedText>
          <Typography sx={infoTextStyle}>{careerPathInfo?.insight}</Typography>
        </Box>
      </Box>

      <Box sx={columnStyle}>
        <Box>
          <HighlightedText>{CAREER_PATH_STRINGS.careerTrackDistribution}</HighlightedText>
          {careerPathInfo?.career_track_distribution.SEMISKILLED && (
            <Graph
              title={CAREER_PATH_STRINGS.semiSkilled}
              percentage={careerPathInfo?.career_track_distribution.SEMISKILLED}
              isFirstItem
            />
          )}
          {careerPathInfo?.career_track_distribution.TRADE && (
            <Graph
              title={CAREER_PATH_STRINGS.tradesTechnical}
              percentage={careerPathInfo?.career_track_distribution.TRADE}
            />
          )}
          {careerPathInfo?.career_track_distribution.MID && (
            <Graph
              title={CAREER_PATH_STRINGS.middleTier}
              percentage={careerPathInfo?.career_track_distribution.MID}
            />
          )}
          {careerPathInfo?.career_track_distribution.UPPER && (
            <Graph
              title={CAREER_PATH_STRINGS.upperTier}
              percentage={careerPathInfo?.career_track_distribution.UPPER}
            />
          )}
          {careerPathInfo?.career_track_distribution.TOP && (
            <Graph
              title={CAREER_PATH_STRINGS.topTier}
              percentage={careerPathInfo?.career_track_distribution.TOP}
            />
          )}
        </Box>

        <Box>
          <InlineText>
            <HighlightedText>{CAREER_PATH_STRINGS.opportunities}</HighlightedText>
            <span>{CAREER_PATH_STRINGS.comingSoon}</span>
          </InlineText>
          <InlineText>
            <HighlightedText>{CAREER_PATH_STRINGS.profiles}</HighlightedText>
            <span>{CAREER_PATH_STRINGS.comingSoon}</span>
          </InlineText>
          <InlineText>
            <HighlightedText>{CAREER_PATH_STRINGS.employers}</HighlightedText>
            <span>{CAREER_PATH_STRINGS.comingSoon}</span>
          </InlineText>
        </Box>
      </Box>
    </Box>
  );
};

const highlightedBlueTextStyle: React.CSSProperties = {
  fontWeight: 700,
  fontSize: '16px',
  color: appColors.color.blue1,
};

const inlineTextContainerStyle: SxProps<Theme> = {
  display: 'flex',
  flexDirection: { xs: 'column', md: 'row' },
  alignItems: { xs: 'flex-start', md: 'center' },
  justifyContent: 'space-between',
  width: '50%',
  mb: { xs: 2, md: 0 },
};

const containerStyle: SxProps<Theme> = {
  display: {
    sm: 'flex',
    md: 'grid',
  },
  flexDirection: 'column',
  gridTemplateColumns: '1fr 1fr',
  gap: '1rem',
  paddingTop: '1rem',
  paddingBottom: '0rem',
};

const columnStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1.5rem',
};

const infoTextStyle: React.CSSProperties = {
  lineHeight: '1.2',
};

const HighlightedText = ({ children }: { children: React.ReactNode }) => (
  <Typography sx={highlightedBlueTextStyle}>{children}</Typography>
);
const InlineText = ({ children }: { children: React.ReactNode }) => (
  <Box sx={inlineTextContainerStyle}>{children}</Box>
);
